import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Card from '@mui/material/Card';
import Button from 'react-bootstrap/Button';
import { useNavigate } from 'react-router-dom';
import './match.scss';
import toast from '../../service/toaster';
import matchService from '../../service/matchService';
import Loader from '../common/Loader';
import { redirectToLogin } from '../../service/authRedirect';

const getSignalFromId = (id = '') => {
    const sum = String(id)
        .split('')
        .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return 58 + (sum % 29);
};

export default function Matches() {
    const navigate = useNavigate();
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const isAdmin = userDetails?.email === 'arifali123454150@gmail.com';

    const [items, setItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastFetchedPage, setLastFetchedPage] = useState(0);
    const [totalMatch, setTotalMatch] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [openMenu, setOpenMenu] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');

    const itemsPerPage = 12;
    const fetchedPages = useRef(new Set());
    const initialLoad = useRef(false);
    const filterInitialized = useRef(false);
    const inFlightPages = useRef(new Set());

    const liveCount = useMemo(() => items.filter((match) => match?.matchStatus === 'Live').length, [items]);
    const readySessionCount = useMemo(
        () => items.filter((match) => Array.isArray(match?.sessions) && match.sessions.length > 0).length,
        [items]
    );

    const updateDeviceMode = useCallback(() => {
        setIsMobile(window.innerWidth <= 768);
    }, []);

    useEffect(() => {
        updateDeviceMode();
        window.addEventListener('resize', updateDeviceMode);
        return () => window.removeEventListener('resize', updateDeviceMode);
    }, [updateDeviceMode]);

    const fetchPage = useCallback(
        async (pageToFetch) => {
            if (loading) return;
            if (fetchedPages.current.has(pageToFetch)) {
                setCurrentPage(pageToFetch);
                return;
            }
            if (!hasMore && pageToFetch > lastFetchedPage) {
                return;
            }
            if (inFlightPages.current.has(pageToFetch)) {
                return;
            }

            inFlightPages.current.add(pageToFetch);
            setLoading(true);
            try {
                const response = await matchService.getMatches(pageToFetch, itemsPerPage, filterStatus);
                const data = (response?.data || []).map((match) => ({ ...match, isReadMore: true }));
                const total = response?.pagination?.total || 0;

                setItems((prev) => (pageToFetch === 1 ? data : [...prev, ...data]));
                fetchedPages.current.add(pageToFetch);
                setHasMore(data.length >= itemsPerPage);
                setLastFetchedPage(pageToFetch);
                setCurrentPage(pageToFetch);
                setTotalMatch(total);

                if (pageToFetch === 1) {
                    toast.success('Matches loaded');
                }
            } catch (err) {
                toast.error("Matches can't be seen");
                redirectToLogin(navigate);
            } finally {
                setLoading(false);
                inFlightPages.current.delete(pageToFetch);
            }
        },
        [filterStatus, hasMore, itemsPerPage, lastFetchedPage, loading, navigate]
    );

    useEffect(() => {
        if (initialLoad.current) return;
        initialLoad.current = true;
        fetchPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!filterInitialized.current) {
            filterInitialized.current = true;
            return;
        }

        fetchedPages.current = new Set();
        setItems([]);
        setLastFetchedPage(0);
        setHasMore(true);
        setCurrentPage(1);
        fetchPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStatus]);

    useEffect(() => {
        if (!isMobile) return;

        const handleScroll = () => {
            if (loading || !hasMore) return;
            const reachBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 150;
            if (reachBottom) {
                fetchPage(lastFetchedPage + 1);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [fetchPage, hasMore, isMobile, lastFetchedPage, loading]);

    const addPrediction = (matchId) => {
        try {
            toast.success(`Match details found: ${matchId}`);
            navigate(`/add-match/${matchId}`);
        } catch (err) {
            toast.error('Cannot view match details');
            redirectToLogin(navigate);
        }
    };

    const goToPrediction = (matchId) => {
        navigate(`/prediction/add/${matchId}`);
    };

    const openMatch = (match) => {
        const hasSessions = Array.isArray(match?.sessions) && match.sessions.length > 0;
        if (!hasSessions) {
            toast.info('Sessions are not published yet for this match');
            return;
        }
        goToPrediction(match._id);
    };

    const handleStatusChange = async (matchId, status) => {
        try {
            if (status === 'delete') {
                await matchService.deleteMatch(matchId);
                setItems((prev) => prev.filter((match) => match._id !== matchId));
                toast.success('Match deleted');
            } else {
                const normalized = status === 'live' ? 'Live' : 'Finished';
                await matchService.updateMatchStatus(matchId, normalized);
                setItems((prev) => prev.map((match) => (match._id === matchId ? { ...match, matchStatus: normalized } : match)));
                toast.success(`Match marked as ${normalized}`);
            }
        } catch (err) {
            toast.error('Could not update match');
        } finally {
            setOpenMenu(null);
        }
    };

    const nextPage = async () => {
        const next = currentPage + 1;
        const needMoreData = items.length < next * itemsPerPage;
        if (needMoreData && hasMore) {
            await fetchPage(next);
        } else if (!needMoreData) {
            setCurrentPage(next);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage((page) => page - 1);
        }
    };

    const totalPages = hasMore
        ? Math.max(1, lastFetchedPage + 1)
        : Math.max(1, Math.ceil(items.length / itemsPerPage));

    const canGoNext = hasMore || currentPage < totalPages;

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = isMobile ? items : items.slice(indexOfFirstItem, indexOfLastItem);

    const convertGMTToIST = (text) => {
        if (!text) return '';
        const regex = /([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{1,2}):(\d{2})\s*GMT/i;
        const match = text.match(regex);
        if (!match) return text.replace(/GMT/gi, '');

        const year = new Date().getFullYear();
        const month = match[1];
        const day = match[2];
        const hour = match[3];
        const minute = match[4];
        const dateStr = `${month} ${day}, ${year} ${hour}:${minute} GMT`;
        const gmtDate = new Date(dateStr);

        if (Number.isNaN(gmtDate.getTime())) return text.replace(/GMT/gi, '');

        const formatter = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Asia/Kolkata',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });

        const istStr = formatter.format(gmtDate).replace(',', '');
        return text.replace(regex, istStr).replace(/GMT/gi, '');
    };

    return (
        <div className="match-page">
            {loading && items.length === 0 && <Loader fullscreen />}

            <div className="page-hero">
                <div>
                    <p className="eyebrow">Live Match Command Center</p>
                    <h1>Pick a live game and jump straight into session analytics.</h1>
                    <p className="hero-sub">
                        Live rooms are highlighted with stronger signal cards so users can quickly find the best match to open.
                    </p>
                </div>

                <div className="hero-badges">
                    <span className="pill">{liveCount} live now</span>
                    <span className="pill ghost">{readySessionCount} ready for prediction</span>
                    <span className="pill ghost">{totalMatch} total matches</span>
                    <select
                        className="filter-select"
                        value={filterStatus}
                        onChange={(event) => setFilterStatus(event.target.value)}
                    >
                        <option value="all">All</option>
                        <option value="Live">Live</option>
                        <option value="Upcoming">Upcoming</option>
                        <option value="Finished">Finished</option>
                    </select>
                </div>
            </div>

            {currentItems.length === 0 && !loading ? (
                <div className="empty-state">
                    <h3>No matches found for this filter.</h3>
                    <p>Try switching status or come back when new sessions are published.</p>
                </div>
            ) : (
                <div className="match-grid">
                    {currentItems.map((match, index) => {
                        const isLive = match?.matchStatus === 'Live';
                        const isFinished = match?.matchStatus === 'Finished';
                        const hasSessions = Array.isArray(match?.sessions) && match.sessions.length > 0;
                        const signalPct = getSignalFromId(match?._id);
                        const cardClass = `match-card-tile ${isLive ? 'is-live' : ''} ${isFinished ? 'is-finished' : 'is-upcoming'}`;

                        return (
                            <Card className={cardClass} key={match._id} style={{ animationDelay: `${0.04 * (index + 1)}s` }}>
                                {isAdmin && (
                                    <div className="admin-menu">
                                        <Button
                                            className="admin-trigger"
                                            onClick={() => setOpenMenu((prev) => (prev === match._id ? null : match._id))}
                                        >
                                            ...
                                        </Button>
                                        {openMenu === match._id && (
                                            <div className="admin-menu-pop">
                                                <Button variant="light" onClick={() => handleStatusChange(match._id, 'live')}>Live</Button>
                                                <Button variant="light" onClick={() => handleStatusChange(match._id, 'finished')}>Finished</Button>
                                                <Button variant="light" className="danger" onClick={() => handleStatusChange(match._id, 'delete')}>Delete</Button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {isLive && (
                                    <div className="live-ribbon">
                                        <span className="live-dot" />
                                        High Crowd Activity
                                    </div>
                                )}

                                <div className="card-top">
                                    <div className="status-row">
                                        <span className={`status-pill ${isLive ? 'live' : isFinished ? 'finished' : 'upcoming'}`}>
                                            {match.matchStatus || 'Upcoming'}
                                        </span>
                                        {match.published_at && <span className="muted">{match.published_at}</span>}
                                    </div>
                                    <div className="title-row" title={match.title}>{match.title}</div>
                                </div>

                                <button className="flags-row" onClick={() => openMatch(match)} type="button">
                                    <div className="flag-card">
                                        <img src={match?.flagImages?.team1} alt={`${match?.team_a || 'Team A'} flag`} />
                                        <span className="team-label">{match?.team_a || 'Team A'}</span>
                                    </div>
                                    <div className="vs-badge">VS</div>
                                    <div className="flag-card">
                                        <img src={match?.flagImages?.team2} alt={`${match?.team_b || 'Team B'} flag`} />
                                        <span className="team-label">{match?.team_b || 'Team B'}</span>
                                    </div>
                                </button>

                                <div className="signal-row">
                                    <div className="signal-meta">
                                        <span>Crowd momentum</span>
                                        <strong>{signalPct}%</strong>
                                    </div>
                                    <div className="signal-track">
                                        <span className="signal-fill" style={{ width: `${signalPct}%` }} />
                                    </div>
                                </div>

                                <div className="meta-row">
                                    {match.content && <span className="pill ghost">{convertGMTToIST(match.content)}</span>}
                                    <span className="pill ghost">{hasSessions ? `${match.sessions.length} sessions` : 'No sessions yet'}</span>
                                </div>

                                {isLive && (
                                    <div className="score-row">
                                        <div>
                                            <span className="muted">{match.team_a}</span>
                                            <strong>{match.team_a_scores || 'Yet to bat'}</strong>
                                        </div>
                                        <div>
                                            <span className="muted">{match.team_b}</span>
                                            <strong>{match.team_b_scores || 'Yet to bat'}</strong>
                                        </div>
                                    </div>
                                )}

                                <div className="btn-row">
                                    <Button
                                        className={`match-cta ${isLive ? 'is-live' : ''}`}
                                        disabled={!hasSessions}
                                        onClick={() => openMatch(match)}
                                    >
                                        {isLive ? 'Join Live Room' : hasSessions ? 'Open Session Board' : 'Awaiting Sessions'}
                                    </Button>

                                    {isAdmin && !hasSessions && (
                                        <Button className="match-cta admin-secondary" onClick={() => addPrediction(match._id)}>
                                            Add Predictions
                                        </Button>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            {!isMobile && (
                <div className="pagination-controls">
                    <Button variant="secondary" onClick={prevPage} disabled={currentPage === 1}>
                        Previous
                    </Button>
                    <span className="page-number">Page {currentPage} of {totalPages}</span>
                    <Button variant="secondary" onClick={nextPage} disabled={!canGoNext}>
                        Next
                    </Button>
                </div>
            )}
        </div>
    );
}
